import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Seo from '@/components/seo/Seo';
import { submitContact } from '@/lib/contactApi';

interface FormData {
  name: string;
  furigana: string;
  email: string;
  phone: string;
  consultationMethod: string;
  preferredDate1: string;
  preferredDate2: string;
  content: string;
  privacyAgreed: boolean;
}

export default function ConsultationConfirmPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const formData = (location.state as { formData?: FormData })?.formData;
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!formData) {
      navigate('/consultation', { replace: true });
    }
  }, [formData, navigate]);

  if (!formData) return null;

  const labelMap: Record<string, string> = {
    name: t('consultation.confirm.fields.name'),
    furigana: t('consultation.confirm.fields.furigana'),
    email: t('consultation.confirm.fields.email'),
    phone: t('consultation.confirm.fields.phone'),
    consultationMethod: t('consultation.confirm.fields.consultationMethod'),
    preferredDate1: t('consultation.confirm.fields.preferredDate1'),
    preferredDate2: t('consultation.confirm.fields.preferredDate2'),
    content: t('consultation.confirm.fields.content'),
  };

  function formatValue(key: string, value: string | boolean): string {
    if (key === 'privacyAgreed') return t('consultation.confirm.privacyAgreed');
    if (key === 'consultationMethod') {
      return value === 'online' ? t('consultation.confirm.fields.online') : t('consultation.confirm.fields.visit');
    }
    if (key === 'content' && !value) return t('consultation.confirm.fields.empty');
    if (key === 'preferredDate2' && !value) return t('consultation.confirm.fields.empty');
    return String(value || '');
  }

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');

    const payload: Record<string, string> = {};
    (Object.keys(formData) as (keyof FormData)[]).forEach((key) => {
      const val = formData[key];
      payload[key] = typeof val === 'boolean' ? (val ? '同意' : '未同意') : val;
    });

    try {
      const resp = await submitContact('consultation', payload);

      if (resp.ok) {
        navigate('/consultation/complete', { replace: true });
      } else {
        setSubmitError(t('consultation.confirm.errorDefault'));
      }
    } catch {
      setSubmitError(t('consultation.confirm.errorNetwork'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Seo route="/consultation/confirm" />
      <Header />
      <main className="pt-[120px] pb-20">
        <div className="max-w-[700px] mx-auto px-8">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-[13px] tracking-[0.06em] mb-10 text-foreground-400">
            <Link to="/" className="hover:text-accent-500 transition-colors">{t('consultation.breadcrumb.home')}</Link>
            <span>{'>'}</span>
            <Link to="/admission" className="hover:text-accent-500 transition-colors">{t('consultation.breadcrumb.admission')}</Link>
            <span>{'>'}</span>
            <Link to="/consultation" className="hover:text-accent-500 transition-colors">{t('consultation.confirm.breadcrumb.consultation')}</Link>
            <span>{'>'}</span>
            <span className="text-accent-600">{t('consultation.confirm.breadcrumb.current')}</span>
          </div>

          <div className="text-center mb-12">
            <h1 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-4">
              {t('consultation.confirm.title')}
            </h1>
            <div className="gold-line mx-auto mb-4"></div>
            <p className="text-[16px] text-foreground-600 tracking-[0.04em]">
              {t('consultation.confirm.lead')}
            </p>
          </div>

          {/* Confirm Fields */}
          <div className="border border-background-200/80 bg-background-100/50 p-8 mb-8">
            <div className="space-y-5">
              {Object.entries(formData).map(([key, value]) => {
                if (key === 'privacyAgreed') return null;
                return (
                  <div key={key} className="border-b border-background-200/40 pb-4 last:border-b-0 last:pb-0">
                    <p className="text-[13px] text-foreground-500 tracking-[0.08em] mb-1 font-label">
                      {labelMap[key] || key}
                    </p>
                    <p className="text-[16px] text-foreground-800 tracking-[0.03em] leading-relaxed">
                      {formatValue(key, value)}
                    </p>
                  </div>
                );
              })}
              <div className="border-b border-background-200/40 pb-4 last:border-b-0 last:pb-0">
                <p className="text-[13px] text-foreground-500 tracking-[0.08em] mb-1 font-label">
                  {t('consultation.confirm.privacyLabel')}
                </p>
                <p className="text-[16px] text-foreground-800 tracking-[0.03em] leading-relaxed">
                  {t('consultation.confirm.privacyAgreed')}
                </p>
              </div>
            </div>
          </div>

          {submitError && (
            <p className="text-[15px] text-red-500 text-center mb-6 tracking-[0.03em] leading-relaxed">{submitError}</p>
          )}

          {/* Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => navigate('/consultation', { state: { formData } })}
              className="px-8 py-3 border border-background-300 text-foreground-600 hover:text-foreground-800 hover:border-foreground-400 text-[15px] tracking-[0.08em] transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('consultation.confirm.editBtn')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="btn-glow px-10 py-3 bg-accent-500/90 hover:bg-accent-500 text-primary-900 text-[15px] tracking-[0.1em] font-medium transition-all duration-300 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {submitting ? t('consultation.confirm.submitting') : t('consultation.confirm.submitBtn')}
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
